
import type { DynamicInvoiceMeasurementChartMeter } from '@weft/api-definition';
import type { ProductLineItem } from '@weft/services';
import { getInstanceUsageMapKey } from '@weft/services';
import { isCommitmentLineItem, isPerUnitProductVariant, isUsageLineItem } from '@weft/services';
import type { CommitmentLineItem,
  CommitmentTierUsageData, LineItemProduct,
  LineItemProductVariant } from '@weft/types';
import { DEFAULT_CURRENCY, INVOICE_REVIEW_STATUSES } from '@weft/types';
import type { Nullable, Optional } from '@weft/utils';
import { getObjectPropertyOrDefault, isNotNullOrUndefined, raise } from '@weft/utils';
import React, { useMemo, useState } from 'react';
import Table from '../../../../../components/design/table/Table';
import { buildCommitmentLineItemPerUnitTableColumns } from '../../../InvoiceViewPage/commitment-line-item-per-unit-table-columns';
import type { CommitmentLineItemPerUnitRow } from '../../../InvoiceViewPage/invoice-types/commitment-line-item-per-unit-row';
import { useDynamicInvoice } from '../../dynamic-invoice-context';
import type { InvoicePerUnitTableRow } from '../../dynamic-invoice-per-unit-table-columns-builder';
import { buildInvoicePerUnitTableColumns } from '../../dynamic-invoice-per-unit-table-columns-builder';
import { UsagePerUnitLineItemBarChart } from '../UsagePerUnitLineItemBarChart';
import { ResetDialog } from './ResetDialog';
import type { ResetLineItemData } from './TiersUsageLineItemBody';

type PerUnitUsageLineItemBodyProps = {
  lineItemProduct: LineItemProduct,
  productLineItem: ProductLineItem,
  productMetersMeasurementChartData: Optional<DynamicInvoiceMeasurementChartMeter>,
  selectedInstanceKeyValue?: string,
};

export const PerUnitUsageLineItemBody:React.FC<PerUnitUsageLineItemBodyProps> = ({
  lineItemProduct,
  productLineItem,
  productMetersMeasurementChartData,
  selectedInstanceKeyValue,
}) => {
  const [resetLineItemData, setResetLineItemData] = useState<Nullable<ResetLineItemData>>(null);

  const onResetClick = (lineItemId: string, productVariantId: string, tierIndex: number) => {
    setResetLineItemData({
      lineItemId,
      productVariantId,
      tierIndex,
    });
  };

  const { dynamicInvoiceData } = useDynamicInvoice();

  const isEditDisabled = dynamicInvoiceData.invoice.reviewStatus === INVOICE_REVIEW_STATUSES.Approved;

  const currency = dynamicInvoiceData.customer.settings?.currency ?? DEFAULT_CURRENCY;

  const columns = useMemo(
    () => buildInvoicePerUnitTableColumns(
      productLineItem,
      getTiersUsageMap(productLineItem, selectedInstanceKeyValue),
      currency,
      isEditDisabled,
      onResetClick,
    ),
    [productLineItem, currency, selectedInstanceKeyValue, isEditDisabled],
  );

  const commitmentColumns = buildCommitmentLineItemPerUnitTableColumns(currency);

  return (
    <div className="mb-4">

      {
        isCommitmentLineItem(productLineItem) && (
          <Table {...{
            rows: buildCommitmentRows(productLineItem, productLineItem.lineItemProduct.variants[0]),
            columns: commitmentColumns,
            tableClassName: 'w-[850px]',
            headerClassName: 'border-b border-b-line w-4',
            cellClassName: 'py-1.5',
          }}
          />
        )
      }
      {
        isUsageLineItem(productLineItem) && (
          <Table {...{
            rows: buildPerUnitProductVariantRows(lineItemProduct, selectedInstanceKeyValue),
            columns,
            tableClassName: 'w-5/6',
            headerClassName: 'border-b border-b-line w-4',
            cellClassName: 'py-1.5',
          }}
          />
        )
      }
      {isNotNullOrUndefined(productMetersMeasurementChartData) && (
        <UsagePerUnitLineItemBarChart
          productMetersMeasurementData={productMetersMeasurementChartData}
          perUnitLineItemProductVariants={lineItemProduct.variants}
          selectedInstanceKeyValue={selectedInstanceKeyValue}
        />
      )}
      {lineItemProduct.variants[0].description && (
        <div className="mt-11 space-y-3">
          <div className="text-soft-title">Product Description</div>
          <div>{lineItemProduct.variants[0].description}</div>
        </div>
      )}
      <ResetDialog
        resetLineItemData={resetLineItemData}
        setResetLineItemData={setResetLineItemData}
      />
    </div>
  );
};

const buildPerUnitProductVariantRows = (lineItemProduct: LineItemProduct, selectedInstanceKeyValue: string | undefined) => {
  return lineItemProduct.variants.map<InvoicePerUnitTableRow>((productVariant) => {
    return {
      ...productVariant,
      usageDataMapKey: isNotNullOrUndefined(selectedInstanceKeyValue) 
        ? getInstanceUsageMapKey(productVariant.productVariantId!, selectedInstanceKeyValue)
        : productVariant.productVariantId!,
    };
  });
};

const getTiersUsageMap = (productLineItem: ProductLineItem, selectedInstanceKeyValue: string | undefined) => {
  if (isCommitmentLineItem(productLineItem)) {
    return productLineItem.commitmentTiersUsageMap;
  }
  
  if (isUsageLineItem(productLineItem)) {
    return isNotNullOrUndefined(selectedInstanceKeyValue) && isNotNullOrUndefined(productLineItem.instanceUsageMap)
      ? productLineItem.instanceUsageMap
      : productLineItem.tiersUsageMap;
  }

  return raise('Invalid line item type');
};

function buildCommitmentRows(lineItem: CommitmentLineItem, productVariant: LineItemProductVariant) {
  if (!isPerUnitProductVariant(productVariant)) {
    return raise('Expected product variant to be a per unit product variant');
  }

  const { price, chunkSize } = productVariant.pricing.priceConfiguration;
  const tiersUsageData = getObjectPropertyOrDefault(lineItem.commitmentTiersUsageMap, productVariant.productVariantId!, []);
  
  return tiersUsageData.map(tierUsageData => buildCommitmentLineItemPerUnitRow(price, chunkSize, tierUsageData));
}

function buildCommitmentLineItemPerUnitRow(price: number, chunkSize: number, tierUsageData: CommitmentTierUsageData): CommitmentLineItemPerUnitRow {
  return {
    rate: price,
    chunkSize,
    ...tierUsageData,
  };
}
